import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import BoardKachel from '../components/board/BoardKachel';
import BoardRollenwahl from '../components/board/BoardRollenwahl';
import { Board, BoardsUebersicht } from '../types';
import { AuthUser } from '../types/auth';
import { BOARD_STORAGE_KEY, begruessung, kachelLeer, standardBoard } from '../lib/board';
import { setAuftragNavigationVerfuegbar, setPartnerNavigationVerfuegbar } from '../lib/navigation';
import { benutzerRollen, sichtbareTabIds } from '../lib/tabRollen';
import { useRechte } from '../hooks/useRechte';
import './DashboardsPage.css';

const API_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000';

interface Props {
  authUser: AuthUser;
}

function headers(): Record<string, string> {
  const token = localStorage.getItem('auth_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function leseGespeicherteRolle(benutzerId: number | undefined): string | null {
  if (benutzerId === undefined) return null;
  try {
    return localStorage.getItem(BOARD_STORAGE_KEY(benutzerId));
  } catch {
    return null;
  }
}

function speichereRolle(benutzerId: number | undefined, rolle: string): void {
  if (benutzerId === undefined) return;
  try {
    localStorage.setItem(BOARD_STORAGE_KEY(benutzerId), rolle);
  } catch { /* Speicher nicht verfügbar: Wahl gilt nur für diese Sitzung */ }
}

/** "2026-10-18" -> "Sonntag, 18. Oktober 2026" (lokales Datum, kein UTC-Versatz). */
function datumLang(iso: string): string {
  const [j, m, t] = iso.split('-').map(Number);
  if (!j || !m || !t) return iso;
  return new Date(j, m - 1, t).toLocaleDateString('de-DE', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  });
}

/** Hauptansicht „Dashboards“: ein Board je Rolle, jede Kachel eine
 *  Entscheidung; Kacheln ohne Daten werden nicht gezeigt. */
const DashboardsPage: React.FC<Props> = ({ authUser }) => {
  const benutzerId = authUser.benutzer_id;
  // Verwaltungs-Tabs, die der Benutzer mit allen seinen Rollen sieht: Kachel-Ziele
  // außerhalb davon werden nicht klickbar dargestellt (null, bis die Rollen geladen sind).
  const { rollen: geladeneRollen, geladen: rollenGeladen } = useRechte();
  const erreichbareTabs = rollenGeladen
    ? sichtbareTabIds(benutzerRollen(geladeneRollen, true, authUser.rolle))
    : null;
  // Chip „Zum Auftrag“ der Belegkarten (kannZuAuftragNavigieren) nur mit Aufträge-Tab.
  // Bewusst im Render statt im Effekt: der Modul-Flag muss stehen, bevor die Kacheln
  // (Kinder) in diesem Durchlauf rendern; der Aufruf ist idempotent.
  if (erreichbareTabs !== null) setAuftragNavigationVerfuegbar(erreichbareTabs.includes('auftraege'));
  if (erreichbareTabs !== null) setPartnerNavigationVerfuegbar(erreichbareTabs.includes('kunden'));
  const [uebersicht, setUebersicht] = useState<BoardsUebersicht | null>(null);
  const [rolle, setRolle] = useState<string | null>(null);
  const [mitarbeiterId, setMitarbeiterId] = useState<number | null>(null);
  const [board, setBoard] = useState<Board | null>(null);
  const [laedt, setLaedt] = useState(true);
  const [fehler, setFehler] = useState(false);
  const [neuLaden, setNeuLaden] = useState(0);
  // Nur die Antwort der jüngsten Anfrage zählt (schnelles Umschalten der Rolle).
  const anfrageRef = useRef(0);
  // Zuletzt gewähltes eigenes Board: Ziel von „Eigene Sicht“.
  const eigeneRolleRef = useRef<string | null>(null);

  const ladeUebersicht = useCallback(async () => {
    setLaedt(true);
    setFehler(false);
    try {
      const res = await axios.get<BoardsUebersicht>(`${API_URL}/api/boards`, { headers: headers() });
      setUebersicht(res.data);
      const start = standardBoard(res.data, leseGespeicherteRolle(benutzerId));
      eigeneRolleRef.current = start;
      setRolle(start);
      setMitarbeiterId(null);
    } catch {
      setFehler(true);
      setLaedt(false);
    }
  }, [benutzerId]);

  useEffect(() => {
    ladeUebersicht();
  }, [ladeUebersicht]);

  useEffect(() => {
    if (!uebersicht) return;
    if (!rolle) {
      anfrageRef.current += 1;
      setBoard(null);
      setLaedt(false);
      return;
    }
    const nr = ++anfrageRef.current;
    setLaedt(true);
    setFehler(false);
    const query = mitarbeiterId !== null ? `?mitarbeiter_id=${mitarbeiterId}` : '';
    axios.get<Board>(`${API_URL}/api/boards/${encodeURIComponent(rolle)}${query}`, { headers: headers() })
      .then(res => {
        if (nr !== anfrageRef.current) return;
        setBoard(res.data);
        setLaedt(false);
      })
      .catch(() => {
        if (nr !== anfrageRef.current) return;
        setFehler(true);
        setLaedt(false);
      });
  }, [uebersicht, rolle, mitarbeiterId, neuLaden]);

  const aktualisieren = useCallback(() => {
    if (!uebersicht) {
      ladeUebersicht();
      return;
    }
    setNeuLaden(n => n + 1);
  }, [uebersicht, ladeUebersicht]);

  const waehleRolle = useCallback((r: string) => {
    setMitarbeiterId(null);
    setRolle(r);
    eigeneRolleRef.current = r;
    speichereRolle(benutzerId, r);
  }, [benutzerId]);

  const waehleMitarbeiter = useCallback((id: number | null) => {
    if (id === null) {
      // „Eigene Sicht“: zurück zum zuvor gewählten eigenen Board.
      setMitarbeiterId(null);
      if (uebersicht) {
        setRolle(eigeneRolleRef.current
          ?? standardBoard(uebersicht, leseGespeicherteRolle(benutzerId)));
      }
      return;
    }
    const m = uebersicht?.mitarbeiter?.find(x => x.mitarbeiter_id === id);
    if (!m) return;
    // Personen-Board des Mitarbeiters (Rolle aus der Mitarbeiterliste, lädt
    // /api/boards/{rolle}?mitarbeiter_id=…); bewusst nicht als eigene Wahl gespeichert.
    setRolle(m.rolle);
    setMitarbeiterId(id);
  }, [uebersicht, benutzerId]);

  const sichtbar = board ? board.kacheln.filter(k => !kachelLeer(k)) : [];
  // Kopfzeile nur aus dem Board der aktuell gewählten Rolle — nicht aus einem
  // noch angezeigten alten Board (Rollenwechsel läuft) und nicht im Fehlerfall.
  const kopfBoard = !fehler && board && board.rolle === rolle ? board : null;
  // Personen-Boards (Arbeiter, Projektmitarbeiter): einspaltiges Raster
  const istPersonenBoard = !!rolle && (uebersicht?.personen_boards ?? []).includes(rolle);

  return (
    <div className={'k-board' + (istPersonenBoard ? ' k-board--arbeiter' : '')}>
      <header className="k-board__kopf">
        <div className="k-board__gruss">
          <h1 className="k-board__titel">{begruessung(kopfBoard?.benutzer.name ?? authUser.name, new Date())}</h1>
          <p className="k-board__datum">
            {kopfBoard ? datumLang(kopfBoard.datum) : ' '}
            {kopfBoard?.mitarbeiter && (
              <span className="k-board__sicht"> · Board von {kopfBoard.mitarbeiter.name}</span>
            )}
          </p>
        </div>
        <button
          type="button"
          className="k-btn k-btn--ghost k-board__aktualisieren"
          onClick={aktualisieren}
          disabled={laedt}
        >
          {laedt ? 'Lädt …' : 'Aktualisieren'}
        </button>
      </header>

      {uebersicht && (
        <BoardRollenwahl
          uebersicht={uebersicht}
          rolle={board?.rolle ?? rolle}
          mitarbeiterId={mitarbeiterId}
          onRolle={waehleRolle}
          onMitarbeiter={waehleMitarbeiter}
        />
      )}

      {fehler ? (
        <div className="k-alert k-alert--danger k-board__hinweis" role="alert">
          <div className="k-alert__msg">Board konnte nicht geladen werden</div>
          <button type="button" className="k-btn k-btn--ghost k-board__nochmal" onClick={aktualisieren}>
            Erneut versuchen
          </button>
        </div>
      ) : !board && laedt ? (
        <p className="k-board__status">Board wird geladen …</p>
      ) : uebersicht && !rolle ? (
        <div className="k-alert k-alert--info k-board__hinweis">
          <div className="k-alert__msg">Für Ihre Rolle gibt es noch kein Dashboard.</div>
        </div>
      ) : board && sichtbar.length === 0 ? (
        <div className="k-alert k-alert--ok k-board__hinweis">
          <div className="k-alert__title">Nichts offen</div>
          <div className="k-alert__msg">Alles erledigt — auf diesem Board wartet gerade nichts auf Sie.</div>
        </div>
      ) : board ? (
        <div className={'k-board__raster' + (laedt ? ' is-laedt' : '')}>
          {sichtbar.map(k => (
            <BoardKachel key={k.key} kachel={k} onChange={aktualisieren} erreichbareTabs={erreichbareTabs} />
          ))}
        </div>
      ) : null}
    </div>
  );
};

export default DashboardsPage;
